import { CaretDownIcon, MoreIcon } from '@persona-id/design-tokens/icons';
import { MouseEvent, ReactChild } from 'react';
import { Route } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import {
  FormReportFlagIssues,
  FormReportFlagIssuesData,
} from 'dashboard/forms/FormReportFlagIssues';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import {
  EXPORTABLE_REPORT_TYPES,
  RECURRING_CA_REPORT_TYPES,
  RECURRING_REPORT_TYPES,
  Report,
} from 'dashboard/models/report';
import { ReportAdverseMedia } from 'dashboard/models/report/adverse-media';
import { ReportBusinessAdverseMedia } from 'dashboard/models/report/business-adverse-media';
import { ReportWatchlist } from 'dashboard/models/report/watchlist';
import { ReportSnapshot } from 'dashboard/models/report-snapshot';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import useConfirmation from 'lib/hooks/useConfirmation';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const Header = styled('div')`
  display: flex;
`;

const NavBackContainer = styled('div')`
  flex-grow: 1;
  display: flex;
  align-items: center;
`;

interface ReportHeaderProps {
  report: Report | ReportSnapshot;
  handlePrintReport?: () => Promise<void>;
  handleFlagIssues?: (data: FormReportFlagIssuesData) => Promise<void>;
  handleRunReportAdhoc?: () => Promise<void>;
  handlePauseReport?: () => Promise<void>;
  handleResumeReport?: () => Promise<void>;
  handleRedactReport?: () => Promise<void>;
}

const navbackText = (report: Report | ReportSnapshot) => {
  switch (report.type) {
    case ApiModel.ReportAdverseMedia: {
      const {
        attributes: { 'name-first': nameFirst, 'name-last': nameLast },
      } = report as ReportAdverseMedia;
      return [nameFirst, nameLast].join(' ');
    }
    case ApiModel.ReportBusinessAdverseMedia: {
      const {
        attributes: { term },
      } = report as ReportBusinessAdverseMedia;
      return term;
    }
    case ApiModel.ReportSnapshotWatchlist:
    case ApiModel.ReportWatchlist: {
      const {
        attributes: { 'name-first': nameFirst, 'name-last': nameLast },
      } = report as ReportWatchlist;
      return [nameFirst, nameLast].join(' ');
    }
    default:
      return 'Report';
  }
};

const ReportHeader = ({
  report,
  handlePrintReport,
  handleFlagIssues,
  handleRunReportAdhoc,
  handlePauseReport,
  handleResumeReport,
  handleRedactReport,
}: ReportHeaderProps) => {
  const navBackLocation = report.id ? `/dashboard/reports/${report.id}` : '/dashboard/reports';

  const isRecurringReport = RECURRING_REPORT_TYPES.includes(report.type);
  const isRecurringCAReport = RECURRING_CA_REPORT_TYPES.includes(report.type);
  const isRedacted = report?.attributes?.['redacted-at'] != null;
  const isReportWithIntervalSeconds =
    report?.attributes?.['report-template-version-interval-seconds'] != null;
  const isSnapshot = report.type === ApiModel.ReportSnapshotWatchlist;

  const isAdHocRunnableReport =
    // basic conditions for adhoc run
    isRecurringReport &&
    !isRedacted &&
    // special condition for CA reports because if there are not recurring, rerunning adhoc may not do anything because
    // we may have unmonitored the CA search to save money.
    (!isRecurringCAReport || isReportWithIntervalSeconds);
  const isExportableReport = EXPORTABLE_REPORT_TYPES.includes(report.type);
  const isPauseable =
    report?.attributes?.['report-template-version-interval-seconds'] != null && !isRedacted;

  const isRedactable = !isRedacted && report.type !== ApiModel.ReportCustomList; // Disallow custom list reports to be redacted for now

  const [flagReportModal, { enable: showFlagReportModal, disable: hideFlagReportModal }] =
    useToggle(false);
  const [showRedactReportModal, { prompt, confirm, cancel }] = useConfirmation();

  const handleRedactReportModal = () => prompt(() => handleRedactReport?.());

  const moreItems: Array<{
    id: string;
    label: ReactChild;
    onClick?: (e: MouseEvent) => void;
    hidden?: boolean;
    variant?: 'basic' | 'danger';
  }> = [
    {
      id: 'report-header__flag-issue',
      onClick: (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        showFlagReportModal();
      },
      label: 'Flag issue',
    },
    {
      id: 'report-header__redact-report',
      onClick: () => handleRedactReportModal(),
      label: 'Redact',
      hidden: !(usePermission(Permission.ReportRedact) && isRedactable),
      variant: 'danger',
    },
  ];

  if (EXPORTABLE_REPORT_TYPES.includes(report.type)) {
    moreItems.unshift({
      id: 'report-header__download-pdf',
      onClick: (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        handlePrintReport?.();
      },
      label: 'Download PDF',
      hidden: !isExportableReport,
    });
  }

  const actionItems = [
    {
      id: 'report-header__run-report',
      onClick: (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        handleRunReportAdhoc?.();
      },
      label: 'Re-run report',
      hidden: !(usePermission(Permission.ReportWrite) && isAdHocRunnableReport),
    },
    {
      id: 'report-header__pause-report',
      onClick: (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (report.attributes['recur-status'] === 'active') {
          handlePauseReport?.();
        } else {
          handleResumeReport?.();
        }
      },
      label: `${
        report.attributes['recur-status'] === 'active' ? 'Pause' : 'Resume'
      } continuous monitoring`,
      hidden: !(usePermission(Permission.ReportPauseWrite) && isPauseable),
    },
  ].filter((item) => ('hidden' in item ? !item.hidden : true));

  const navBackElement = report && <NavBack to={navBackLocation}>{navbackText(report)}</NavBack>;

  return (
    <div>
      <Header>
        <NavBackContainer>
          <SentryRoutes>
            {report && (
              <>
                <Route path='identity-matches/active/:idx' element={navBackElement} />
                <Route path='identity-matches/dismissed/:idx' element={navBackElement} />
                <Route path='entity-matches/active/:idx' element={navBackElement} />
                <Route path='entity-matches/dismissed/:idx' element={navBackElement} />
              </>
            )}
            <Route path='*' element={<NavBack to='/dashboard/reports'>All Reports</NavBack>} />
          </SentryRoutes>
        </NavBackContainer>
        {!isSnapshot && (
          <HStack>
            <DropdownMenu
              trigger={
                <Button
                  id='report-header__actions-button'
                  tabIndex={0}
                  data-test='report-header__action-button--dropdown'
                  variant='basic'
                  width='auto'
                  suffixIcon={<CaretDownIcon />}
                >
                  Run Actions
                </Button>
              }
            >
              {actionItems.map(({ id, onClick, label }) => (
                <DropdownMenu.Item data-test={id} id={id} onClick={onClick} key={id}>
                  {label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu>
            <DropdownMenu
              trigger={
                <Button
                  id='report-header__more-button'
                  tabIndex={0}
                  data-test='report-header__button--dropdown'
                  variant='basic'
                  width='auto'
                  prefixIcon={<MoreIcon />}
                >
                  More
                </Button>
              }
            >
              {moreItems
                .filter((item) => ('hidden' in item ? !item.hidden : true))
                .map(({ id, onClick, label, variant }) => (
                  <DropdownMenu.Item
                    data-test={id}
                    id={id}
                    onClick={onClick}
                    key={id}
                    variant={variant}
                  >
                    {label}
                  </DropdownMenu.Item>
                ))}
            </DropdownMenu>
          </HStack>
        )}
        <ModalWrapper isOpen={flagReportModal} handleCloseModal={hideFlagReportModal}>
          <ModalContainer size='lg'>
            <ModalHeader onClose={hideFlagReportModal}>Flag an issue with this report</ModalHeader>
            <ModalBody>
              <p>
                If you see an issue with the content in this report, you can flag it to our QA team
                for review.
              </p>
              <FormReportFlagIssues
                onSubmit={(data: FormReportFlagIssuesData) => {
                  handleFlagIssues?.(data);
                  hideFlagReportModal();
                }}
                handleCancel={hideFlagReportModal}
              />
            </ModalBody>
          </ModalContainer>
        </ModalWrapper>
        <ModalWrapper isOpen={showRedactReportModal} handleCloseModal={cancel}>
          <ConfirmationModal
            cancelAction={cancel}
            confirmAction={confirm}
            header='Redact this report’s PII?'
            confirmActionText='Redact'
          >
            Personally identifiable information will be redacted. This action is permanent and can’t
            be undone.
          </ConfirmationModal>
        </ModalWrapper>
      </Header>
    </div>
  );
};

export default ReportHeader;
